import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["row"];

  // Appends a new row to the list.
  append(event) {
    this.#setCurrentRowFrom(event);

    const nextRow = this.#create();

    this.#hideAppendButtonOfCurrentRow();

    this.element.appendChild(nextRow);
  }

  // Removes a row from (or hides a persisted one in) the list.
  remove(event) {
    this.#setCurrentRowFrom(event);

    if (this.#isLast(this.currentRow)) {
      this.#showAppendButtonOfPreviousRow();
    }

    this.#destroy(this.currentRow);
  }

  #setCurrentRowFrom(event) {
    this.currentRow = event.target.closest("div[data-fields-for-target]");
  }

  /**
   * Creates a row to append.
   *
   * Rows are always appended to the bottom of the list and based on the
   * current row (i.e., the last row and the only one with an append button
   * enabled).
   */
  #create() {
    const row = this.currentRow.cloneNode(true);

    row.querySelector("div.attribute-errors")?.remove();

    const oldIndex = this.#indexOf(this.currentRow);
    const newIndex = oldIndex + 1;

    row.querySelectorAll("input").forEach((input) => {
      input.value = "";
      input.autofocus = true;
      input.name = input.name.replace(`[${oldIndex}]`, `[${newIndex}]`); // `foo[1][bar]` -> `foo[2][bar]`.
      input.id = input.id.replace(`_${oldIndex}_`, `_${newIndex}_`); // `foo_1_bar` -> `foo_2_bar`.
    });

    row.querySelectorAll("label").forEach((label) => {
      label.htmlFor = label.htmlFor.replace(`_${oldIndex}_`, `_${newIndex}_`);
      label.textContent = label.textContent.replace(` ${oldIndex + 1}`, ` ${newIndex + 1}`);
    });

    this.#showAppendButton(row);
    this.#showRemoveButton(row);

    return row;
  }

  /**
   * Destroys a row.
   *
   * If the current row is persisted, we need to append a `_destroy` attribute
   * with a truthy value and hide the row, not remove it from the DOM.
   */
  #destroy(row) {
    // If the current row is not persisted, we can yeet it.
    if (!this.#isPersisted(this.currentRow)) {
      return this.currentRow.remove();
    }

    // Otherwise, we need to append a _destroy attribute with the correct name
    // for Rails params.
    const input = this.currentRow.querySelector("input");
    const prefix = input.name.slice(0, input.name.lastIndexOf("[")); // "foo[bar][1][baz]" -> "foo[bar][1]"
    const hidden = document.createElement("input");
    hidden.type = "hidden";
    hidden.name = `${prefix}[_destroy]`;
    hidden.value = 1;

    this.currentRow.appendChild(hidden);

    this.#hide(this.currentRow);
  }

  #hideAppendButtonOfCurrentRow() {
    this.#hideAppendButton(this.currentRow);
  }

  /**
   * Shows the append button the previous visible row.
   *
   * If the previous row was persisted and destroyed, it's hidden, not removed,
   * and we need to skip over it.
   */
  #showAppendButtonOfPreviousRow() {
    let previous = this.#previousSibling(this.currentRow);

    while (previous && this.#isHidden(previous)) {
      previous = this.#previousSibling(previous);
    }

    this.#showAppendButton(previous);
  }

  #showAppendButton(row) {
    const button = this.#findAppendButton(row);

    this.#show(button);
  }

  #showRemoveButton(row) {
    const button = this.#findRemoveButton(row);

    this.#show(button);
  }

  #hideAppendButton(row) {
    const button = this.#findAppendButton(row);

    this.#hide(button);
  }

  #hideRemoveButton(row) {
    const button = this.#findRemoveButton(row);

    this.#hide(button);
  }

  #findAppendButton(row) {
    return row.querySelector('button[data-action="fields-for#append"]');
  }

  #findRemoveButton(row) {
    return row.querySelector('button[data-action="fields-for#remove"]');
  }

  #hide(element) {
    element.classList.add("hidden");
  }

  #show(element) {
    element.classList.remove("hidden");
  }

  #indexOf(row = null) {
    return this.rowTargets.indexOf(row);
  }

  #isFirst(row) {
    return this.#indexOf(row) == 0;
  }

  #isLast(row) {
    return this.#indexOf(row) == this.rowTargets.length - 1;
  }

  #isHidden(row) {
    return row.classList.contains("hidden");
  }

  /**
   * Returns true if the row represents a persisted record.
   *
   * Rails outputs a hidden text input as a persister row's next element
   * sibling with the record's id.
   */
  #isPersisted(row) {
    const next = row.nextElementSibling;

    if (!next) {
      return false;
    }

    return (
      next.tagName.toLowerCase() === "input" && next.type == "hidden" && next.name.includes("[id]")
    );
  }

  #previousSibling(row) {
    return this.rowTargets[this.#indexOf(row) - 1];
  }
}
